import { v4 as uuidv4 } from 'uuid';
import { PRESET_COLORS } from '../../../helpers/draggable-input/helper';
import { KanbanProspectListPayload } from '../../../types';
import { BoardData, KanbanLane, LaneColor } from '../types';

/**
 * Normalizes a status value into a lowercase, alphanumeric key for consistent lookups.
 *
 * @param value - Raw status value that can be string, number or null
 * @returns A normalized, lowercase, alphanumeric key string
 */
export const normalizeStatusKey = (value?: string | number | null) =>
  (value ?? '')
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/**
 * Ensures the "Unassigned" option appears first in the provided items array.
 *
 * @param items - Array of items that may contain an "Unassigned" value
 * @returns A new array with "Unassigned" moved to the first position if present
 */
export const ensureUnassignedFirst = <T extends { value?: string }>(
  items: T[],
): T[] => {
  const unassignedIndex = items.findIndex(
    (item) => item.value?.toLowerCase() === 'unassigned',
  );
  if (unassignedIndex <= 0) return items; // Already first or not found

  const unassigned = items[unassignedIndex];
  const others = items.filter((_, index) => index !== unassignedIndex);
  return [unassigned, ...others];
};

/**
 * Builds a list of lookup keys from a raw value, including normalized and canonical forms.
 *
 * @param rawValue - Original value from which to derive lookup keys
 * @param canonicalMap - Map of normalized keys to their canonical equivalents
 * @returns An array of unique lookup keys
 */
export const getLookupKeys = (
  rawValue?: string | number | null,
  canonicalMap?: Map<string, string>,
) => {
  const keys = new Set<string>();

  if (rawValue) {
    keys.add(rawValue.toString().toLowerCase());
  }

  const normalizedKey = normalizeStatusKey(rawValue);
  if (normalizedKey) {
    keys.add(normalizedKey);
    if (canonicalMap) {
      const canonicalKey = canonicalMap.get(normalizedKey) || normalizedKey;
      if (canonicalKey) {
        keys.add(canonicalKey);
      }
    }
  }

  return Array.from(keys);
};

/**
 * Builds a lookup map from status-like keys to arrays of prospects.
 *
 * @param source - Source object where keys map to arrays of prospects
 * @param canonicalMap - Map of normalized keys to their canonical equivalents
 * @returns A Map where each lookup key is mapped to its corresponding prospects array
 */
export const buildProspectLookup = (
  source: Record<string, any>,
  canonicalMap: Map<string, string>,
) => {
  const lookup = new Map<string, any[]>();

  if (!source) {
    return lookup;
  }

  Object.entries(source).forEach(([key, value]) => {
    if (!Array.isArray(value)) return;
    const lookupKeys = getLookupKeys(key, canonicalMap);
    lookupKeys.forEach((lookupKey) => {
      if (!lookupKey) return;
      lookup.set(lookupKey, value);
    });
  });

  return lookup;
};

/**
 * Finds the first matching list of prospects for the provided lookup keys.
 *
 * @param lookup - Map of lookup keys to arrays of prospects
 * @param keys - Keys to try in order when searching for prospects
 * @returns The first matching prospects array found, or undefined
 */
export const findProspectsForKeys = (
  lookup: Map<string, any[]>,
  keys: string[],
) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const key of keys) {
    // eslint-disable-next-line no-continue
    if (!key) continue;
    const prospects = lookup.get(key);
    if (Array.isArray(prospects)) {
      return prospects;
    }
  }
  return undefined;
};

/**
 * Formats a lane title, applying special casing for specific values.
 *
 * @param title - Original lane title
 * @returns A formatted lane title string
 */
export const formatLaneTitle = (title?: string | null) => {
  if (!title) {
    return '';
  }

  const normalized = title.replace(/[\s-]/g, '').toLowerCase();
  if (normalized === 'inactive') {
    return 'InActive';
  }

  return title;
};

// ** Constant
export const initialBoardData: BoardData = {
  lanes: [],
};

export const transformKanbanData = (
  payload: KanbanProspectListPayload[] = [],
  selectedCategoryId: number | null = null,
): BoardData => {
  if (!payload?.length) {
    return initialBoardData;
  }

  const lanes: KanbanLane[] = [];

  // console.log({ payload, selectedCategoryId });
  let filteredPayload = payload.filter(
    (item) => item?.id === selectedCategoryId,
  );

  // Then filter by selected category ID if one is selected
  if (selectedCategoryId !== null) {
    filteredPayload = filteredPayload.filter(
      (item) => item.id === selectedCategoryId,
    );
  }

  filteredPayload.forEach((item) => {
    // Filter out hidden categories (status === 0) and sort by sortOrder
    const visibleCategories = (item?.metadata?.categories || []).filter(
      (category) => category.status === 1,
    );
    const sortedCategories = ensureUnassignedFirst(
      [...visibleCategories].sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
      ),
    );
    sortedCategories.forEach((category, index) => {
      // item?.metadata?.categories?.forEach((category, index) => {
      // Set backgroundColor - use category.color if available, otherwise default to #FEF4F6
      const backgroundColor = category?.color || '#FEF4F6';
      let color: LaneColor;
      if (category.color) {
        // Try to find matching PRESET_COLOR
        const matchingPresetColor = PRESET_COLORS?.find(
          (preset) => preset?.fill === category?.color,
        );
        color = matchingPresetColor || PRESET_COLORS[0];
      } else {
        [color] = PRESET_COLORS;
      }

      const uniqueId = uuidv4();

      lanes.push({
        cards: [],
        currentPage: 1,
        // ** Old ID logic
        id: `${category.value || 'lane'}-${item.id}-${index}`,
        // id: uniqueId,
        label: `${category.status ?? 0}/${category.sortOrder ?? 0}`,
        valueKey: category.value || 'Untitled Lane',
        style: {
          width: 260,
          backgroundColor,
        },
        color,
        title: formatLaneTitle(category.value || 'Untitled Lane'),
      });
    });
  });

  const fallbackLanes = lanes.length ? lanes : initialBoardData.lanes;

  return {
    lanes: fallbackLanes,
  };
};

export const defaultKanbanPageSize = 25;

/**
 * Get sequence matching lane title and extract sequenceId/outcomeId
 */
export const getSequenceInfo = (
  card: any,
  laneTitle?: string,
  isOutcomeKanban?: boolean,
  allOutcomes?: any[],
) => {
  if (!isOutcomeKanban || !card?.sequenceProspects || !laneTitle) {
    return { seqId: null, outcomeId: null };
  }

  const seqs = Array.isArray(card.sequenceProspects) ? card.sequenceProspects : [];
  const normalized = laneTitle.toLowerCase();

  // Find matching sequence by category name/label
  let match = seqs.find(
    (s: any) =>
      s?.category?.name?.toLowerCase() === normalized ||
      s?.category?.label?.toLowerCase() === normalized,
  );

  // Fallback: match by outcome name
  if (!match && allOutcomes) {
    const outcome = allOutcomes.find((o: any) => o?.name?.toLowerCase() === normalized);
    if (outcome?.id) match = seqs.find((s: any) => s?.categoryId === outcome.id);
  }

  const seq = match || seqs[0];
  if (!seq) return { seqId: null, outcomeId: null };

  return {
    seqId: seq?.currentStep?.sequence?.id || seq?.sequence?.id || seq?.sequenceId,
    outcomeId: seq?.categoryId,
  };
};

/**
 * Get unique key for tracking card moves (uses sequenceId)
 */
export const getCardUniqueKey = (
  card: any,
  laneTitle?: string,
  isOutcomeKanban?: boolean,
  allOutcomes?: any[],
): string => {
  const contactId = String(card?.id ?? card?.contactId ?? '');
  if (!isOutcomeKanban) return contactId;
  const { seqId } = getSequenceInfo(card, laneTitle, isOutcomeKanban, allOutcomes);
  return seqId ? `${contactId}_${seqId}` : contactId;
};

/**
 * Merge cards with same outcome when adding to lane
 */
export const mergeCardsWithSameOutcome = (
  existingCards: any[],
  newCard: any,
  targetLaneTitle?: string,
  isOutcomeKanban?: boolean,
  allOutcomes?: any[],
): any[] => {
  if (!isOutcomeKanban || !newCard?.sequenceProspects) {
    return [...existingCards, newCard];
  }

  const contactId = String(newCard?.id ?? newCard?.contactId ?? '');
  const { outcomeId: newOutcomeId } = getSequenceInfo(
    newCard,
    targetLaneTitle,
    isOutcomeKanban,
    allOutcomes,
  );
  if (!newOutcomeId) return [...existingCards, newCard];

  const existingIndex = existingCards.findIndex((card) => {
    if (String(card?.id ?? card?.contactId ?? '') !== contactId) return false;
    return (
      getSequenceInfo(card, targetLaneTitle, isOutcomeKanban, allOutcomes).outcomeId ===
      newOutcomeId
    );
  });

  if (existingIndex >= 0) {
    const existing = existingCards[existingIndex];
    const allSeqs = [
      ...(Array.isArray(existing?.sequenceProspects) ? existing.sequenceProspects : []),
      ...(Array.isArray(newCard.sequenceProspects) ? newCard.sequenceProspects : []),
    ];

    const seqMap = new Map();
    allSeqs.forEach((seq: any) => {
      const seqId =
        seq?.currentStep?.sequence?.id || seq?.sequence?.id || seq?.sequenceId;
      if (seqId) seqMap.set(seqId, seq);
    });

    const updated = [...existingCards];
    updated[existingIndex] = {
      ...existing,
      sequenceProspects: Array.from(seqMap.values()),
    };
    return updated;
  }

  return [...existingCards, newCard];
};
